// FloatingWindow — the app's movable window chrome (Unreal window model).
//
// Rebecca's feedback (2026-08-06): the Settings window couldn't be moved, so
// seeing a scene-colour change meant closing the window that set it. This is
// the general fix: any panel wrapped in a FloatingWindow can be dragged around
// the shell, resized, POPPED OUT into a real OS window (second monitor), and
// brought back. Geometry and mode persist per window id (windowStore).
//
// Three modes, one component:
//   docked    the host's own layout position (children rendered where the host
//             put them — Settings centres itself, the editor fills the
//             viewport region). No window chrome beyond the title bar.
//   floating  position:fixed, draggable by the title bar, resizable by the
//             corner grip, click-to-front.
//   popped    children PORTAL into a `window.open` document — a real OS window
//             the user can put on another monitor. The React tree stays OWNED
//             by this document: module stores, subscriptions and the session
//             all keep working. NOTE the children do REMOUNT across the mode
//             flip (a portal is a different fiber type at the same tree slot),
//             so durable UI state must live in stores or be re-fed via props —
//             see App.jsx's initialActiveBP={editorSurface} for the pattern.
//             Closing the popup returns the window to the mode it popped
//             out of (floating or docked).
//
// WHY A PORTAL AND NOT A ROUTE: a popup on its own app boot has its OWN
// module singletons, so it edits a DIFFERENT session's state — a Settings
// popup would retune another palette store, and the old blueprint-editor
// popup (?editor_popup=1, a full second boot) had divergent graph state and
// always opened on the body graph regardless of what the main window showed.
// The portal keeps the SAME component instance, same stores, same session;
// the popup is just where the pixels land. The blueprint editor itself pops
// out this way now (App.jsx uses useWindowPopOut on 'editor-surface'); the
// second-boot route is gone.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { windowStore, viewportSize, dragRect, effectiveMode, placeOnOpen, popupPlacement,
  toggleMaximize,
  rectToWindowUnits, resizeByScreenDelta, MIN_W, MIN_H } from '../lib/windowStore.js'
import { cumulativeZoom } from '../lib/uiZoomCoords'
import { installFieldDoubleClick } from '../lib/fieldDoubleClick.js'
import { UI_FONT } from './PipelineGlyph.jsx'

export function useWindowState(id) {
  const [state, setState] = useState(() => windowStore.get(id))
  useEffect(() => {
    // Re-sync on subscribe. ensureGeometry (below) centres the window from a
    // useLayoutEffect, which fires — and notifies — BEFORE this passive effect
    // runs, so the notify lands with no listener and the component keeps
    // rendering at the null/default (0,0) geometry until the next store change
    // (a drag) happens to repaint it. Reading the store once here catches that
    // first geometry set, so the window opens where it was placed.
    setState(windowStore.get(id))
    return windowStore.subscribe(() => setState(windowStore.get(id)))
  }, [id])
  return state
}

/**
 * Title-bar drag for a window whose CHROME lives in the host (the blueprint
 * editor's own surface header doubles as its window bar — a second stacked
 * title bar is exactly the clutter this feature exists to remove). Returns
 * pointer-down props to spread on the bar element; they no-op unless the
 * window is floating.
 */
/**
 * Begin a title-bar drag. ONE implementation: this logic existed twice, once in
 * useWindowDrag (for a window whose chrome lives in the host) and once inside
 * FloatingWindow's own bar, and both copies added a raw screen delta to the
 * window's local coordinates — so fixing it in one place would have left the
 * other wrong, which is how they came to be worth merging.
 *
 * The zoom is read from the dragged element rather than from a global, matching
 * the menus (uiZoomCoords): it is the cumulative zoom at that node, so a window
 * inside a subtree that undoes the root zoom would still track the cursor.
 *
 * `mode` is the EFFECTIVE mode (windowStore.effectiveMode), passed in rather
 * than re-read from the store: a `dockable={false}` host renders a stored
 * 'docked' as floating, and reading the store here instead is what made
 * Settings and Rig Preview undraggable on a fresh profile. The caller already
 * knows the answer — it is what it rendered — so it states it.
 */
function beginBarDrag(id, e, mode) {
  if (mode !== 'floating') return
  if (e.button !== 0) return
  // Buttons inside the bar are actions, not handles.
  if (e.target.closest('button')) return
  e.preventDefault()
  const start = windowStore.get(id)
  const zoom = cumulativeZoom(e.currentTarget)
  const sx = e.clientX, sy = e.clientY
  const move = (ev) => {
    windowStore.set(id, dragRect(start, ev.clientX - sx, ev.clientY - sy, zoom, window))
  }
  const up = () => {
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
}

export function useWindowDrag(id, { dockable = true } = {}) {
  const win = useWindowState(id)
  const mode = effectiveMode(win.mode, dockable)
  const onPointerDown = useCallback((e) => beginBarDrag(id, e, mode), [id, mode])
  return {
    win,
    dragProps: {
      onPointerDown,
      // The cursor is read off the SAME mode the guard uses: a 'move' cursor on
      // a window that refuses to move is the tell this bug shipped behind.
      style: mode === 'floating' ? { cursor: 'move', touchAction: 'none' } : null,
    },
  }
}

/**
 * Edge/corner resize for a host-chromed window. Returns a factory:
 * `resize('nw')` gives the pointer-down handler for that grip.
 *
 * The starting rect is read from the DOM, not the store: a window may be
 * rendering on host defaults (`editorWin.w ?? 720`) with nothing written to
 * the store yet, and starting from a null would make the first drag jump.
 */
export function useWindowResizeDir(id) {
  return useCallback((dir) => (e) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    const frame = e.currentTarget.parentElement
    if (!frame) return
    // getBoundingClientRect is in ZOOMED pixels and the store is in window
    // units, so the start rect is converted before anything is written — an
    // unconverted one made the window leap by (zoom - 1) x its position the
    // instant a grip was grabbed, before any actual resizing.
    const zoom = cumulativeZoom(frame)
    const start = rectToWindowUnits(frame.getBoundingClientRect(), zoom)
    const sx = e.clientX, sy = e.clientY
    const move = (ev) => {
      windowStore.set(id, resizeByScreenDelta(start, dir, ev.clientX - sx, ev.clientY - sy, zoom))
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }, [id])
}

/** Back-compat: the bottom-right grip alone. */
export function useWindowResize(id) {
  const resize = useWindowResizeDir(id)
  return useCallback((e) => resize('se')(e), [resize])
}

const EDGE = 6     // hit width of a side; thin enough not to eat content clicks
const CORNER = 14  // corners win over sides, as in every OS window manager

// Sides stop short of the corners so the corner grips stay grabbable.
const GRIPS = [
  { dir: 'n',  cursor: 'ns-resize',   css: { top: 0, left: CORNER, right: CORNER, height: EDGE } },
  { dir: 's',  cursor: 'ns-resize',   css: { bottom: 0, left: CORNER, right: CORNER, height: EDGE } },
  { dir: 'w',  cursor: 'ew-resize',   css: { left: 0, top: CORNER, bottom: CORNER, width: EDGE } },
  { dir: 'e',  cursor: 'ew-resize',   css: { right: 0, top: CORNER, bottom: CORNER, width: EDGE } },
  { dir: 'nw', cursor: 'nwse-resize', css: { top: 0, left: 0, width: CORNER, height: CORNER } },
  { dir: 'ne', cursor: 'nesw-resize', css: { top: 0, right: 0, width: CORNER, height: CORNER } },
  { dir: 'sw', cursor: 'nesw-resize', css: { bottom: 0, left: 0, width: CORNER, height: CORNER } },
  { dir: 'se', cursor: 'nwse-resize', css: { bottom: 0, right: 0, width: CORNER, height: CORNER } },
]

/**
 * The eight resize grips of a floating window — four sides, four corners,
 * dragged like any OS window.
 *
 * Render as a direct child of the window frame (the grips are absolutely
 * positioned against it, and the drag reads that element's rect for its
 * starting geometry). The north grip deliberately overlays the title bar's
 * top few px: that is where a user aims to resize from the top, and the grip
 * stops the event so the bar's move-drag never sees it.
 *
 * The south-east grip keeps the visible diagonal hatch — the affordance that
 * tells you the window is resizable at all — and keeps the original
 * `window-grip-<id>` testid.
 */
export function WindowResizeGrips({ id, gripTestId }) {
  const resize = useWindowResizeDir(id)
  return (
    <>
      {GRIPS.map(({ dir, cursor, css }) => (
        <div
          key={dir}
          data-testid={dir === 'se' ? (gripTestId ?? `window-grip-${id}`) : `window-resize-${dir}-${id}`}
          onPointerDown={resize(dir)}
          title="Resize"
          style={{
            position: 'absolute', zIndex: 5, cursor, touchAction: 'none', ...css,
            ...(dir === 'se' ? {
              background: 'linear-gradient(135deg, transparent 50%, var(--m-line-hi) 50%)',
              borderBottomRightRadius: 8,
            } : null),
          }}
        />
      ))}
    </>
  )
}

/** Copy every style the main document carries into the popup document, so the
 *  portalled DOM renders with the app's real look (tokens, palette, fonts). */
function copyStyles(from, to) {
  // Palette + theme ride CSS custom properties and data attributes on <html>.
  to.documentElement.className = from.documentElement.className
  to.documentElement.dataset.palette = from.documentElement.dataset.palette ?? ''
  to.documentElement.style.cssText = from.documentElement.style.cssText
  for (const node of from.querySelectorAll('style, link[rel="stylesheet"]')) {
    to.head.appendChild(node.cloneNode(true))
  }
}

/**
 * The 'popped' mode's machinery, as a hook — so a HOST-CHROMED window (the
 * blueprint editor surface, whose own header doubles as its title bar, like
 * useWindowDrag/useWindowResizeDir above) can pop into a real OS window
 * without adopting FloatingWindow's chrome. FloatingWindow itself uses this
 * same hook.
 *
 * Contract:
 *  - `popOut()` opens an about:blank OS window in the SAME browsing context
 *    group (the portal needs synchronous access to its document), copies the
 *    app's styles in, sets windowStore mode 'popped', and remembers the mode
 *    it left so closing the popup RETURNS there (docked stays docked,
 *    floating stays floating — never a silent mode change).
 *  - `popupBody` is the popup document's <body> while popped, else null. The
 *    host renders `createPortal(content, popupBody)` — same React instance,
 *    same module stores, only the DOM output moves.
 *  - `popIn()` closes the OS window; the pagehide handler restores the mode.
 *  - Host unmount closes the popup (no stranded OS window on a dead portal).
 */
export function useWindowPopOut(id, { title = '', defaultSize = { w: 720, h: 520 }, dockable = true } = {}) {
  const popupRef = useRef(null)
  const prevModeRef = useRef('floating')
  const [popupBody, setPopupBody] = useState(null)

  const popOut = useCallback(() => {
    // Already popped: focus the existing OS window instead of replacing it.
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus()
      return
    }
    const cur = windowStore.get(id)
    // Remember where to come back to. 'popped' can't be a previous mode (a
    // reload persists it as floating — see windowStore) but guard anyway.
    prevModeRef.current = cur.mode === 'popped' ? 'floating' : cur.mode
    const w = cur.w ?? defaultSize.w
    const h = cur.h ?? defaultSize.h
    // about:blank in the SAME browsing context group — the portal needs
    // synchronous access to the popup document.
    // Opens WHERE THE WINDOW IS, on the app's own monitor. Popping out is a
    // change of frame, not a move, so the OS window takes the floating
    // window's place and size; a docked window has no place of its own and
    // centres over the app. Never left to the browser: an unpositioned popup
    // cascades from the PRIMARY display's origin, so an app on a second
    // monitor pops out onto the other screen — indistinguishable, to the user,
    // from the popup never opening (popupPlacement).
    //
    // The rect is scaled by the UI zoom because the store holds window units
    // and the popup is placed in the viewport's own CSS px (see viewportSize).
    //
    // Through effectiveMode, not the raw stored mode. A `dockable={false}`
    // host (Settings, Rig Preview, Version Control, the material graph…) never
    // writes 'floating' — nothing can, since the float button is gated on
    // `dockable` — so its stored mode is 'docked' for the window's whole life
    // while it renders, drags and resizes as a floating one. Reading the raw
    // mode here therefore threw away the rect for exactly the windows most
    // likely to be popped out, and they centred over the app instead of
    // opening where they sat. This is the question effectiveMode exists to be
    // the single answer to.
    const { zoom } = viewportSize()
    const here = (cur.x == null || cur.y == null || effectiveMode(cur.mode, dockable) === 'docked')
      ? null
      : { x: cur.x * zoom, y: cur.y * zoom }
    const { left, top } = popupPlacement(w, h, window, here)
    const popup = window.open('', `aos_window_${id}`, `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no`)
    if (!popup) {
      console.warn(`[FloatingWindow] popup blocked for '${id}' — staying put`)
      return
    }
    popup.document.title = title
    copyStyles(document, popup.document)
    // Page-wide input rules are per window; give the popup its own.
    installFieldDoubleClick(popup)
    // PERCENTAGES, not vw/vh: copyStyles brings the app's UI-scale zoom over
    // with the root's inline style, and a viewport unit is an absolute length
    // the zoom then multiplies — so at the default 1.1 the popup's body was
    // 110% of its own window and `overflow: hidden` quietly clipped a tenth of
    // the panel off the right and bottom edges. A percentage of the root fills
    // the window at any scale. (Set after copyStyles: it rewrites the root's
    // cssText wholesale.)
    popup.document.documentElement.style.height = '100%'
    popup.document.body.style.cssText = 'margin:0;width:100%;height:100%;overflow:hidden;background:var(--m-bg)'
    popupRef.current = popup
    setPopupBody(popup.document.body)
    windowStore.set(id, { mode: 'popped' })
    // Return to the previous mode when the OS window closes — pagehide fires
    // on close AND on the opener navigating it away; both mean "no popup".
    const onGone = () => {
      popupRef.current = null
      setPopupBody(null)
      // Only demote if still popped — the host may have closed meanwhile.
      if (windowStore.get(id).mode === 'popped') windowStore.set(id, { mode: prevModeRef.current })
    }
    popup.addEventListener('pagehide', onGone)
  }, [id, title, defaultSize.w, defaultSize.h, dockable])

  const popIn = useCallback(() => { popupRef.current?.close() }, [])

  // The host unmounting (onClose, character switch…) must not strand an OS
  // window showing a dead portal.
  useEffect(() => () => { popupRef.current?.close() }, [])

  // Keep the popup's palette in sync if the user retunes it while popped —
  // palette changes rewrite <html> data attrs in the main document only.
  useEffect(() => {
    if (!popupBody) return
    const obs = new MutationObserver(() => {
      const doc = popupRef.current?.document
      if (doc) {
        doc.documentElement.className = document.documentElement.className
        doc.documentElement.dataset.palette = document.documentElement.dataset.palette ?? ''
        doc.documentElement.style.cssText = document.documentElement.style.cssText
      }
    })
    obs.observe(document.documentElement, { attributes: true })
    return () => obs.disconnect()
  }, [popupBody])

  return { popupBody, popOut, popIn }
}

export default function FloatingWindow({
  id,
  title,
  onClose,
  // Testid for the bar's close ✕. Defaults to `window-close-${id}`; a host
  // whose old in-content close button carried a contract testid (Settings'
  // `appearance-close`) passes it here so removing that button doesn't break
  // the coverage that clicks it — the bar ✕ is now the one true close control.
  closeTestId,
  // Rendered inside the title bar, before the window buttons — hosts put
  // section labels or their own affordances here.
  barExtra = null,
  // Default size used the first time this window floats (before any persist).
  defaultSize = { w: 720, h: 520 },
  // Where the window sits on its FIRST open, before the user drags it.
  // 'centre' (default) matches every existing window; 'right' is for panels
  // you operate while watching the viewport, which must not cover it.
  defaultAnchor = 'centre',
  // 'docked' hosts render children in their own layout; the bar then offers
  // "float". Hosts that are ALWAYS floating (Settings) pass false to skip the
  // docked mode entirely.
  dockable = true,
  // Extra style for the window body wrapper (floating/popped modes).
  bodyStyle = null,
  children,
}) {
  const win = useWindowState(id)
  const mode = effectiveMode(win.mode, dockable)
  const { popupBody, popOut, popIn } = useWindowPopOut(id, { title, defaultSize, dockable })

  // Placement is the STORE's (placeOnOpen): persisted geometry is untrusted
  // input, and every window has to be validated the same way — including the
  // editor surface, which renders its own frame and never comes through here.
  const ensureGeometry = useCallback(
    () => placeOnOpen(id, { w: defaultSize.w, h: defaultSize.h, anchor: defaultAnchor }),
    [id, defaultSize.w, defaultSize.h, defaultAnchor],
  )

  useLayoutEffect(() => { if (mode !== 'docked') ensureGeometry() }, [mode, ensureGeometry])

  // ── Drag (title bar) ─────────────────────────────────────────────────────
  const onBarPointerDown = useCallback((e) => beginBarDrag(id, e, mode), [id, mode])

  // Resize is <WindowResizeGrips/> (all four sides + corners) — rendered in
  // the frame below. Pop-out/pop-in is useWindowPopOut above.

  const buttons = (
    // flexShrink 0: a long title must never push the window controls out past
    // the frame. In a narrow DOCKED host (the ENG-1046 Generate region at its
    // 300px floor) the nowrap title did exactly that, and the ✕ rendered over
    // the neighbouring panel — outside the window it belongs to, so the click
    // target left the window too.
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      {dockable && mode === 'floating' && (
        <WindowBarButton testId={`window-dock-${id}`} title="Dock back into the layout" onClick={() => windowStore.set(id, { mode: 'docked' })}>⇤</WindowBarButton>
      )}
      {dockable && mode === 'docked' && (
        <WindowBarButton testId={`window-float-${id}`} title="Float — drag this window anywhere" onClick={() => { ensureGeometry(); windowStore.set(id, { mode: 'floating' }); windowStore.bringToFront(id) }}>❐</WindowBarButton>
      )}
      {mode === 'floating' && (
        <WindowBarButton
          testId={`window-max-${id}`}
          title={win.restore ? 'Restore size' : 'Maximize'}
          onClick={() => toggleMaximize(id)}
        >{win.restore ? '🗗' : '🗖'}</WindowBarButton>
      )}
      {mode !== 'popped' && (
        <WindowBarButton testId={`window-popout-${id}`} title="Open in its own OS window (drag it to another monitor)" onClick={popOut}>⧉</WindowBarButton>
      )}
      {mode === 'popped' && (
        <WindowBarButton testId={`window-popin-${id}`} title="Bring back into the app" onClick={popIn}>⇥</WindowBarButton>
      )}
      {onClose && (
        <WindowBarButton testId={closeTestId ?? `window-close-${id}`} title="Close" onClick={onClose}>✕</WindowBarButton>
      )}
    </span>
  )

  const bar = (
    <div
      data-testid={`window-bar-${id}`}
      onPointerDown={onBarPointerDown}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '5px 10px',
        background: 'var(--m-panel)', borderBottom: '1px solid var(--m-line)',
        flexShrink: 0, userSelect: 'none',
        cursor: mode === 'floating' ? 'move' : 'default',
        touchAction: 'none',
        // minWidth 0 + overflow hidden: a flex item's default `min-width: auto`
        // refuses to shrink below its content, so nowrap text in here grew the
        // bar past the frame instead of being clipped by it.
        minWidth: 0, overflow: 'hidden',
      }}
    >
      <span style={{
        fontFamily: UI_FONT, fontSize: 9, letterSpacing: '.14em', color: 'var(--m-fg)',
        whiteSpace: 'nowrap',
        // Ellipsize rather than push. The title is the elastic element here —
        // the controls to its right are not negotiable.
        minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {title}
      </span>
      {barExtra && (
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {barExtra}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }} />
      {buttons}
    </div>
  )

  const frame = (content, style) => (
    <div
      data-testid={`window-${id}`}
      data-mode={mode}
      onMouseDown={() => windowStore.bringToFront(id)}
      style={style}
    >
      {bar}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', ...(bodyStyle ?? {}) }}>
        {content}
      </div>
      {mode === 'floating' && <WindowResizeGrips id={id} />}
    </div>
  )

  if (mode === 'popped' && popupBody) {
    // The popup fills its own OS window; the bar keeps the pop-in control.
    return createPortal(
      frame(children, {
        width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'var(--m-panel)', color: 'var(--m-fg)', position: 'relative',
      }),
      popupBody,
    )
  }

  if (mode === 'floating') {
    return frame(children, {
      position: 'fixed', left: win.x ?? 0, top: win.y ?? 0,
      width: win.w ?? defaultSize.w, height: win.h ?? defaultSize.h,
      zIndex: win.z, display: 'flex', flexDirection: 'column',
      background: 'var(--m-panel)', color: 'var(--m-fg)',
      border: '1px solid var(--m-line-hi)', borderRadius: 8,
      boxShadow: '0 20px 60px rgba(0,0,0,0.55)', overflow: 'hidden',
    })
  }

  // Docked: the host's own layout box.
  return frame(children, {
    width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
    background: 'var(--m-panel)', color: 'var(--m-fg)', position: 'relative',
  })
}

export function WindowBarButton({ testId, title, onClick, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      title={title}
      style={{
        background: 'transparent', border: '1px solid var(--m-line-hi)',
        color: 'var(--m-fg-2)', borderRadius: 4, padding: '2px 7px',
        fontSize: 11, fontFamily: UI_FONT, cursor: 'pointer', lineHeight: 1.4,
      }}
    >{children}</button>
  )
}
